import type { UsageByKeyEntry, UsageRequestLogEntry } from '@nadoutong/contracts';
import { Landmark, LogIn, LogOut, RefreshCw, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDisplayRate } from '../../api/useBilling.ts';
import { useCatalog } from '../../api/useCatalog.ts';
import {
  useUsageByKey,
  useUsageDaily,
  useUsageRequests,
  useUsageSummary,
} from '../../api/useUsage.ts';
import { apiKeyDisplayName } from '../../components/apiKeyName.ts';
import { DailyUsageChart } from '../../components/DailyUsageChart.tsx';
import {
  formatTokens,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  unitAwareTokenCell,
} from '../../components/formatters.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import type { I18nKey } from '../../i18n/index.ts';
import { useT } from '../../i18n/index.ts';
import type { DateRangePreset } from '../../ui/index.ts';
import { DateRangePicker } from '../../ui/index.ts';
import type { DayRange } from './format.ts';
import {
  dayRangeToIsoWindow,
  formatCompactCount,
  formatNullablePercent,
  formatRatio,
  nullableRatio,
  rollingDayRange,
  testIdSlug,
  todayLocalDay,
  uncachedInput,
} from './format.ts';
import type { Translate } from './shared.tsx';
import { MAX_RANGE_SPAN_DAYS, PanelHeader, SummaryCard, UsageError } from './shared.tsx';

/**
 * Self-scoped `/usage`, reached when the session lacks
 * `org:usage:read`. Strictly personal: summary KPIs, a daily in/out
 * trend, per-key aggregates, and recent billed-request metadata. The
 * admin dashboard endpoint is never called from this branch — the
 * permission fork in `Usage.tsx` is what keeps that true, so this file
 * must not grow an org-wide query.
 *
 * The cache KPIs the admin side reads straight off its dashboard
 * response have no member-scoped endpoint, so they are folded here
 * from the by-key rows; `summarizeMemberCache` is the whole of that
 * derivation and deliberately yields `null` — not 0 — where there is
 * no denominator.
 *
 * Everything rendered here is billing metadata. Prompts and responses
 * are never collected, and nothing on this page may start implying
 * otherwise.
 */

/** Preset selected on arrival; also the fallback if none resolves. */
const DEFAULT_RANGE = 'today';

/**
 * The member window choices. They are deliberately NOT the admin set —
 * no month-to-date, and no long-form names — but the ids share the
 * admin's lowercase form so the two screens never spell the same
 * window two ways.
 *
 * The set reaches 90 days because `MAX_RANGE_SPAN_DAYS` is 90: without
 * a `90d` preset the widest window the UI accepts was reachable from
 * the admin screen only, and a member auditing the quarter they are
 * billed on had no way to select it — not even by hand, since the
 * custom picker is capped at the same 90.
 *
 * The labels are the shared `usage.range.*` keys, not the admin's
 * long-form `usage.admin.range.*` ones: the label is reused verbatim
 * inside the summary details below, where a long window name would
 * bury the sentence it sits in.
 *
 * The shortest one is `today` because that is what it queries: the
 * local calendar day, 00:00 → 23:59:59.999. Since it is also the
 * DEFAULT preset, a `24h` name would misdescribe the very first
 * numbers the page shows.
 */
function memberRangePresets(today: string, t: Translate): readonly DateRangePreset[] {
  return [
    { id: 'today', label: t('usage.range.today'), range: rollingDayRange(today, 1) },
    { id: '7d', label: t('usage.range.7d'), range: rollingDayRange(today, 7) },
    { id: '30d', label: t('usage.range.30d'), range: rollingDayRange(today, 30) },
    { id: '90d', label: t('usage.range.90d'), range: rollingDayRange(today, 90) },
  ];
}

/**
 * The four-up token split reuses the shared metric nouns; the map keeps
 * the two call sites (`by key` and `by request`) from drifting apart.
 */
const CACHE_METRIC_KEYS: Readonly<Record<'in' | 'out' | 'write' | 'read', I18nKey>> = {
  in: 'usage.metric.in',
  out: 'usage.metric.out',
  write: 'usage.metric.write',
  read: 'usage.metric.read',
};

type MemberCacheSummary = {
  readonly successRequests: number;
  readonly cacheHitRequests: number;
  readonly cacheEligibleInputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
  readonly requestCacheHitRate: number | null;
  readonly tokenCacheHitRate: number | null;
  readonly cacheReadWriteRatio: number | null;
};

export function MemberUsage(): JSX.Element {
  const t = useT();
  // A preset selection is stored id-only so its window follows "today"
  // across renders; only a custom pick pins concrete endpoints.
  const [rangeState, setRangeState] = useState<{
    readonly presetId: string | null;
    readonly custom: DayRange | null;
  }>({ presetId: DEFAULT_RANGE, custom: null });
  const today = todayLocalDay();
  const rangePresets = useMemo(() => memberRangePresets(today, t), [today, t]);
  const activePreset =
    rangeState.presetId !== null
      ? rangePresets.find((preset) => preset.id === rangeState.presetId)
      : undefined;
  const dayRange: DayRange = activePreset?.range ?? rangeState.custom ?? rollingDayRange(today, 1);
  const queryRange = useMemo(() => dayRangeToIsoWindow(dayRange), [dayRange]);
  const rangeLabel = activePreset?.label ?? `${dayRange.from} - ${dayRange.to}`;
  const summaryQ = useUsageSummary(queryRange);
  const dailyQ = useUsageDaily(queryRange);
  const byKeyQ = useUsageByKey(queryRange);
  const requestsQ = useUsageRequests({
    ...queryRange,
    limit: 50,
    offset: 0,
    detail: 'summary',
  });
  const displayRateQ = useDisplayRate();
  const catalogQ = useCatalog();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const summary = summaryQ.data;
  const cacheSummary = useMemo(
    () => summarizeMemberCache(byKeyQ.data?.keys ?? []),
    [byKeyQ.data?.keys],
  );
  const brandedSkuNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const sku of catalogQ.data?.skus ?? []) map.set(sku.sku_id, sku.sku_name);
    return map;
  }, [catalogQ.data?.skus]);

  return (
    <section data-testid="usage-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          <h2 className="font-display text-display text-ink">{t('usage.member.title')}</h2>
          <p className="text-body leading-6 text-ink-low">{t('usage.member.subtitle')}</p>
        </div>
        <DateRangePicker
          value={dayRange}
          onChange={(range, presetId) => {
            setRangeState(
              presetId !== null ? { presetId, custom: null } : { presetId: null, custom: range },
            );
          }}
          presets={rangePresets}
          activePresetId={rangeState.presetId}
          maxSpanDays={MAX_RANGE_SPAN_DAYS}
          ariaLabel={t('usage.range.aria_label')}
          labels={{
            prevMonth: t('usage.range.prev_month'),
            nextMonth: t('usage.range.next_month'),
            presets: t('usage.range.presets_aria_label'),
          }}
          testId="usage-range-picker"
        />
      </header>

      {summaryQ.isError ? (
        <UsageError
          onRetry={() => {
            void summaryQ.refetch();
          }}
          testId="usage-member-error"
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <SummaryCard
            label={t('usage.summary.tokens_in')}
            value={
              summary !== undefined
                ? t('usage.summary.tokens_value', {
                    tokens: formatCompactCount(summary.totalInputTokens),
                  })
                : '-'
            }
            detail={t('usage.member.summary.scope_detail', { range: rangeLabel })}
            icon={LogIn}
            testId="usage-summary-input-tokens"
          />
          <SummaryCard
            label={t('usage.summary.tokens_out')}
            value={
              summary !== undefined
                ? t('usage.summary.tokens_value', {
                    tokens: formatCompactCount(summary.totalOutputTokens),
                  })
                : '-'
            }
            detail={
              summary !== undefined
                ? t('usage.requests_count', { count: formatTokens(summary.totalRequests) })
                : t('usage.summary.loading')
            }
            icon={LogOut}
            testId="usage-summary-output-tokens"
          />
          <SummaryCard
            label={t('usage.summary.cache_write')}
            value={
              summary !== undefined
                ? t('usage.summary.tokens_value', {
                    tokens: formatCompactCount(summary.totalCacheWriteTokens),
                  })
                : '-'
            }
            detail={t('usage.member.summary.cache_write_detail', { range: rangeLabel })}
            icon={Save}
            testId="usage-summary-cache-write-tokens"
          />
          <SummaryCard
            label={t('usage.summary.cache_read')}
            value={
              summary !== undefined
                ? t('usage.summary.tokens_value', {
                    tokens: formatCompactCount(summary.totalCacheReadTokens),
                  })
                : '-'
            }
            detail={t('usage.member.summary.cache_read_detail', { range: rangeLabel })}
            icon={RefreshCw}
            testId="usage-summary-cache-read-tokens"
          />
          <SummaryCard
            label={t('usage.summary.spend')}
            value={
              summary !== undefined
                ? formatUsdMicrosOrCentsAsCnyDisplay(
                    summary.totalCostUsdMicros,
                    summary.totalCostUsdCents,
                    cnyPerUsd,
                  )
                : '-'
            }
            detail={t('usage.member.summary.spend_detail')}
            icon={Landmark}
            testId="usage-summary-cost"
          />
          <SummaryCard
            label={t('usage.summary.cache_hit')}
            value={formatNullablePercent(cacheSummary.requestCacheHitRate)}
            detail={t('usage.member.summary.cache_hit_detail', {
              rate: formatNullablePercent(cacheSummary.tokenCacheHitRate),
              count: formatTokens(cacheSummary.successRequests),
            })}
            icon={RefreshCw}
            testId="usage-summary-member-scope"
          />
        </div>
      )}

      <article className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5">
        <PanelHeader
          title={t('usage.member.trend.title')}
          subtitle={t('usage.member.trend.subtitle')}
        />
        {dailyQ.isLoading ? (
          <Skeleton className="h-64" />
        ) : dailyQ.isError ? (
          <PanelError
            message={t('usage.error.load')}
            retryLabel={t('usage.retry')}
            onRetry={() => {
              void dailyQ.refetch();
            }}
            testId="usage-member-daily-error"
          />
        ) : (
          <DailyUsageChart buckets={dailyQ.data?.days ?? []} testId="usage-daily-chart" />
        )}
      </article>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MemberKeyPanel
          keys={byKeyQ.data?.keys ?? []}
          isLoading={byKeyQ.isLoading}
          isError={byKeyQ.isError}
          onRetry={() => {
            void byKeyQ.refetch();
          }}
          cnyPerUsd={cnyPerUsd}
          brandedSkuNameById={brandedSkuNameById}
        />
        <MemberRequestPanel
          rows={requestsQ.data?.rows ?? []}
          isLoading={requestsQ.isLoading}
          isError={requestsQ.isError}
          onRetry={() => {
            void requestsQ.refetch();
          }}
        />
      </div>
    </section>
  );
}

/**
 * Display label for a by-key row's SKU (epic #898 P6 #913). Rows are one
 * per key×SKU; the stored `skuName` is the CANONICAL `namespace/model`,
 * so it is brand-aliased at render time through the catalog (`sku_id` →
 * brand-aliased `sku_name`). Legacy rows (null `skuId`) fall back to
 * their historical channel NAME.
 *
 * `channelSlug` is deliberately NOT a fallback layer: a slug is an
 * internal routing handle, and printing it would tell a tenant how the
 * upstream is partitioned (AGENTS.md: 租户对上游路由是黑盒). A row with
 * nothing presentable renders the em-dash placeholder instead — an
 * unlabelled row, not a labelled internal.
 */
function byKeyRowModelLabel(
  entry: UsageByKeyEntry,
  brandedSkuNameById: ReadonlyMap<string, string>,
): string {
  if (typeof entry.skuId === 'string' && entry.skuId.length > 0) {
    const branded = brandedSkuNameById.get(entry.skuId);
    if (branded !== undefined) return branded;
  }
  if (typeof entry.skuName === 'string' && entry.skuName.length > 0) return entry.skuName;
  if (typeof entry.channelName === 'string' && entry.channelName.length > 0) {
    return entry.channelName;
  }
  return '—';
}

/** Stable per-row identity now that a key can appear once per SKU. */
function byKeyRowId(entry: UsageByKeyEntry): string {
  return `${entry.apiKeyId}:${entry.skuId ?? entry.channelId ?? 'legacy'}`;
}

/** Top-6 of the caller's own keys, each with its token split, cache rates and cost. */
function MemberKeyPanel(props: {
  readonly keys: readonly UsageByKeyEntry[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly cnyPerUsd: number | null;
  readonly brandedSkuNameById: ReadonlyMap<string, string>;
}): JSX.Element {
  const t = useT();
  return (
    <article className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5">
      <PanelHeader
        title={t('usage.member.keys.title')}
        subtitle={t('usage.member.keys.subtitle')}
      />
      {props.isLoading ? (
        <Skeleton className="h-44" />
      ) : props.isError ? (
        <PanelError
          message={t('usage.error.load')}
          retryLabel={t('usage.retry')}
          onRetry={props.onRetry}
          testId="usage-member-keys-error"
        />
      ) : props.keys.length === 0 ? (
        <p className="text-body text-ink-low">{t('usage.member.keys.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {props.keys.slice(0, 6).map((key) => (
            <div
              key={byKeyRowId(key)}
              className="grid grid-cols-1 gap-3 border-b border-line pb-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto]"
              data-testid={`usage-member-key-${testIdSlug(byKeyRowId(key))}`}
            >
              <div className="min-w-0">
                <p
                  className="truncate font-medium text-ink"
                  data-testid={`usage-member-key-name-${testIdSlug(byKeyRowId(key))}`}
                >
                  {apiKeyDisplayName(key.apiKeyName, key.apiKeyPrefix, t)}
                </p>
                <p
                  className="truncate font-mono text-caption text-ink-low"
                  data-testid={`usage-member-key-model-${testIdSlug(byKeyRowId(key))}`}
                >
                  {byKeyRowModelLabel(key, props.brandedSkuNameById)}
                </p>
                <p className="font-mono text-caption text-ink-low">
                  {t('usage.member.keys.activity', {
                    ok: formatTokens(key.successRequests),
                    hits: formatTokens(key.cacheHitRequests),
                  })}
                </p>
              </div>
              <div className="grid min-w-56 grid-cols-4 gap-2 text-right">
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.in)}
                  value={formatCompactCount(
                    // By-key rows carry cacheEligibleInputTokens. Request
                    // rows below do not, so they keep raw inputTokens.
                    uncachedInput({
                      cacheEligibleInputTokens: key.cacheEligibleInputTokens,
                      cacheReadTokens: key.cacheReadTokens,
                      cacheWriteTokens: key.cacheWriteTokens,
                    }),
                  )}
                />
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.out)}
                  value={formatCompactCount(key.outputTokens)}
                />
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.write)}
                  value={formatCompactCount(key.cacheWriteTokens)}
                />
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.read)}
                  value={formatCompactCount(key.cacheReadTokens)}
                />
                <p className="col-span-4 font-mono text-caption text-ink-low">
                  {t('usage.member.keys.cache_rates', {
                    request: formatNullablePercent(key.requestCacheHitRate),
                    token: formatNullablePercent(key.tokenCacheHitRate),
                    ratio: formatRatio(key.cacheReadWriteRatio),
                  })}
                </p>
                <p className="col-span-4 font-mono text-body text-forest">
                  {formatUsdMicrosOrCentsAsCnyMetered(
                    key.costUsdMicros,
                    key.costUsdCents,
                    props.cnyPerUsd,
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

/** One cell of the four-up in/out/write/read grid the two member panels share. */
function KeyCacheMetric({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="font-mono text-mono-label uppercase text-ink-low">{label}</span>
      <span className="font-mono text-caption font-medium text-ink">{value}</span>
    </span>
  );
}

/** Latest 8 billed requests — metadata only, never prompt or response content. */
function MemberRequestPanel(props: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <article className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5">
      <PanelHeader
        title={t('usage.member.requests.title')}
        subtitle={t('usage.member.requests.subtitle')}
      />
      {props.isLoading ? (
        <Skeleton className="h-44" />
      ) : props.isError ? (
        <PanelError
          message={t('usage.error.load')}
          retryLabel={t('usage.retry')}
          onRetry={props.onRetry}
          testId="usage-member-requests-error"
        />
      ) : props.rows.length === 0 ? (
        <p className="text-body text-ink-low">{t('usage.member.requests.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {props.rows.slice(0, 8).map((row) => (
            <div
              key={row.id}
              className="grid grid-cols-1 gap-3 border-b border-line pb-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto]"
              data-testid={`usage-request-row-${testIdSlug(row.requestId)}`}
            >
              <div className="min-w-0">
                <p className="truncate font-mono text-body text-ink">{row.model}</p>
                <p className="font-mono text-caption text-ink-low">{row.requestId}</p>
              </div>
              {/* #1670: unit-metered rows dash instead of showing fake 0s. */}
              <div className="grid min-w-56 grid-cols-4 gap-2 text-right">
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.in)}
                  value={unitAwareTokenCell(row, formatCompactCount(row.inputTokens))}
                />
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.out)}
                  value={unitAwareTokenCell(row, formatCompactCount(row.outputTokens))}
                />
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.write)}
                  value={unitAwareTokenCell(row, formatCompactCount(row.cacheWriteTokens))}
                />
                <KeyCacheMetric
                  label={t(CACHE_METRIC_KEYS.read)}
                  value={unitAwareTokenCell(row, formatCompactCount(row.cacheReadTokens))}
                />
                <p className="col-span-4 font-mono text-caption text-ink-low">
                  {t('usage.member.requests.status', { code: row.statusCode })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * Fold the by-key rows into the org-style cache KPIs. Rates are
 * computed from the SUMMED numerators and denominators, not averaged
 * across keys — a key with three requests must not weigh as much as
 * one with thirty thousand.
 */
function summarizeMemberCache(keys: readonly UsageByKeyEntry[]): MemberCacheSummary {
  const totals = keys.reduce(
    (acc, key) => ({
      successRequests: acc.successRequests + key.successRequests,
      cacheHitRequests: acc.cacheHitRequests + key.cacheHitRequests,
      cacheEligibleInputTokens: acc.cacheEligibleInputTokens + key.cacheEligibleInputTokens,
      cacheReadTokens: acc.cacheReadTokens + key.cacheReadTokens,
      cacheWriteTokens: acc.cacheWriteTokens + key.cacheWriteTokens,
    }),
    {
      successRequests: 0,
      cacheHitRequests: 0,
      cacheEligibleInputTokens: 0,
      cacheReadTokens: 0,
      cacheWriteTokens: 0,
    },
  );
  return {
    ...totals,
    requestCacheHitRate: nullableRatio(totals.cacheHitRequests, totals.successRequests),
    tokenCacheHitRate: nullableRatio(totals.cacheReadTokens, totals.cacheEligibleInputTokens),
    cacheReadWriteRatio: nullableRatio(totals.cacheReadTokens, totals.cacheWriteTokens),
  };
}
